/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */
import { Injectable, inject, OnDestroy, signal, WritableSignal, NgZone } from '@angular/core';
import { AuthService as Auth0Service, User, LogoutOptions } from '@auth0/auth0-angular';
import { OSKHttpsSuccessResponse } from '@oskey/core';
import {
  OSKCreateUserDTO,
  OSKCountry,
  OSKUser,
  OSKUpdateProfileDTO
} from '@oskey/core/types';
import { OSKFirebaseAuthService, OSKFirebaseHttpsService } from '@oskey/firebase';
import { UserCredential } from 'firebase/auth';
import { Observable, Subscription, of, firstValueFrom, combineLatest, from } from 'rxjs';
import {
  switchMap,
  filter,
  take,
  map,
  catchError,
  finalize,
  tap,
  timeout,
  distinctUntilChanged
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { OSKTranslateService } from '@oskey/translate';
import { OSKToastService } from '@oskey/ui';

@Injectable({
  providedIn: 'root'
})
export class OSKAuthService implements OnDestroy {
  private readonly auth0 = inject(Auth0Service);
  private readonly firebase: OSKFirebaseAuthService = inject(OSKFirebaseAuthService);
  private readonly firebaseHttps: OSKFirebaseHttpsService = inject(OSKFirebaseHttpsService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly toast = inject(OSKToastService);
  private readonly translate = inject(OSKTranslateService);
  private readonly subscriptions = new Subscription();
  private appStateConsumed = false;

  public readonly isAuthenticated$: Observable<boolean> = this.auth0.isAuthenticated$;
  public readonly user$: Observable<User | null | undefined> = this.auth0.user$;
  public readonly isLoading: WritableSignal<boolean> = signal(true);

  constructor() {
    this.subscriptions.add(
      this.auth0.error$.subscribe((err) => {
        if (err) {
          console.error('Auth0 error:', err);
          this.isLoading.set(false);
          const isAccessDenied = (err as { error?: string })?.error === 'access_denied';
          const msg = isAccessDenied
            ? this.translate.instant('auth.signIn.accessDeniedError')
            : this.translate.instant('auth.signIn.authenticationError');
          this.toast.danger(msg, undefined, 7000);
        }
      })
    );

    this.subscriptions.add(
      this.auth0.idTokenClaims$
        .pipe(
          distinctUntilChanged((prev, curr) => prev?.__raw === curr?.__raw),
          filter((claims): claims is { __raw: string } => {
            if (!claims) {
              this.isLoading.set(false);
              return false;
            }
            return true;
          }),
          tap(() => this.isLoading.set(true)),
          switchMap((claims) =>
            from(this.exchangeAuth0TokenForFirebaseToken(claims.__raw)).pipe(
              switchMap((result) => {
                if (!result?.firebaseToken) {
                  return of(null);
                }
                return combineLatest({
                  userCredential: from(
                    this.firebase.signInWithCustomToken(result.firebaseToken)
                  ),
                  auth0User: this.auth0.user$.pipe(
                    filter((u): u is User => !!u),
                    take(1)
                  )
                }).pipe(
                  switchMap(({ userCredential, auth0User }) => {
                    if (!userCredential || !auth0User) {
                      return of(null);
                    }
                    return from(this.getUserByUid(userCredential.user.uid)).pipe(
                      switchMap((existingUser) => {
                        if (existingUser) {
                          return of(undefined);
                        }
                        const nameParts = (auth0User.name || '').trim().split(' ');
                        const firstName =
                          auth0User.given_name || nameParts[0] || auth0User.nickname || 'Admin';
                        const lastName =
                          auth0User.family_name || nameParts.slice(1).join(' ') || 'User';

                        const userDTO: OSKCreateUserDTO = {
                          userId: userCredential.user.uid,
                          email: auth0User.email ?? '',
                          publicProfile: {
                            firstName,
                            lastName,
                            userId: userCredential.user.uid
                          }
                        };

                        return from(this.createUser(userDTO));
                      }),
                      map(() => userCredential)
                    );
                  })
                );
              }),
              switchMap((userCredential) => {
                if (!userCredential) {
                  return of(null);
                }
                return this.auth0.appState$.pipe(
                  timeout({ each: 1000, with: () => of(undefined) }),
                  take(1),
                  catchError(() => of(undefined)),
                  tap((appState) => {
                    const rawTarget =
                      (appState as { returnUrl?: string; target?: string } | undefined)
                        ?.returnUrl ?? appState?.target;
                    const redirectTarget = !this.appStateConsumed ? rawTarget : undefined;
                    if (appState) {
                      this.appStateConsumed = true;
                    }

                    if (this.isValidInternalTarget(redirectTarget)) {
                      if (this.router.url !== redirectTarget) {
                        this.zone.run(() => this.router.navigateByUrl(redirectTarget));
                      }
                      return;
                    }

                    if (!this.isValidInternalTarget(this.router.url)) {
                      this.zone.run(() => this.router.navigateByUrl('/admin'));
                    }
                  })
                );
              }),
              catchError((err) => {
                console.error('Firebase sign-in error:', err);
                this.toast.danger(
                  this.translate.instant('auth.signIn.firebaseSignInError'),
                  undefined,
                  7000
                );
                return of(null);
              }),
              finalize(() => this.isLoading.set(false))
            )
          )
        )
        .subscribe()
    );
  }

  signUpWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return this.firebase.signUpWithEmailAndPassword(email, password);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loginWithRedirect(targetUrl?: string): void {
    const candidate = targetUrl || this.router.url;
    const target = this.isValidInternalTarget(candidate) ? candidate : '/admin';
    this.auth0.loginWithRedirect({
      appState: { returnUrl: target }
    });
  }

  private isValidInternalTarget(url?: string): url is string {
    return (
      !!url &&
      url.startsWith('/') &&
      !url.startsWith('//') &&
      !url.startsWith('/auth') &&
      url !== '/'
    );
  }
  signOut(): Promise<void> {
    return this.firebase.signOut().then(() => {
      const logout$ = this.auth0.logout({
        logoutParams: { returnTo: window.location.origin }
      } as LogoutOptions);
      return firstValueFrom(logout$ || of(undefined));
    });
  }

  private exchangeAuth0TokenForFirebaseToken(
    token: string
  ): Promise<{ firebaseToken: string; newUser: boolean; userId: string } | null> {
    return this.firebaseHttps
      .call<
        { token: string },
        { firebaseToken: string; newUser: boolean; userId: string }
      >('core-exchangeAuth0Token', { token })
      .then((response) => response.data ?? null);
  }
  signUpWithEmailLink(email: string, continueUrl?: string): Promise<boolean> {
    return this.firebase.signUpWithEmailLink(email, continueUrl);
  }
  signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return this.firebase.signInWithEmailAndPassword(email, password);
  }

  verifyResetPasswordCode(code: string): Promise<string> {
    return this.firebase.verifyPasswordResetCode(code);
  }

  resetPassword(code: string, password: string): Promise<void> {
    return this.firebase.resetPassword(code, password);
  }

  confirmSignIn(): Promise<UserCredential | null> {
    return this.firebase.confirmSignIn();
  }

  createUser(userDTO: OSKCreateUserDTO): Promise<void> {
    return this.firebase.setDoc(userDTO, 'users', userDTO.userId);
  }

  updateProfile(profileDTO: OSKUpdateProfileDTO): Promise<void> {
    return this.firebase.updateProfile(profileDTO);
  }

  getUserByUid(uid: string): Promise<OSKUser | undefined> {
    return this.firebase.getUserByUid(uid);
  }

  updateUserProfileAndPhoneNumber(
    data: OSKCreateUserDTO
  ): Promise<OSKHttpsSuccessResponse<undefined>> {
    return this.firebaseHttps.call<OSKCreateUserDTO, undefined>(
      'user-updateUserProfileAndPhoneNumber',
      data
    );
  }
  sendResetPasswordEmail(email: string): Promise<void> {
    return this.firebase.sendPasswordResetEmail(email);
  }

  public getCountriesNoAuth(): Promise<OSKHttpsSuccessResponse<OSKCountry[]>> {
    return this.firebaseHttps.call<never, OSKCountry[]>('core-getCountriesNoAuth');
  }
}
